<template>
    <fieldset v-if="!zipCode">
        <div class="jumbotron text-center text-md-left">
            <div class="container with-transparency">
                <div class="row justify-content-md-center">
                    <div class="col-md-auto">
                        <div class="content">
                            <h1 class="border-0">Find the Lowest Heating Oil Price in Your Town</h1>
                            <div v-if="zipCodeError" v-text="zipCodeError" class="alert alert-danger"></div>
                            <div class="form-row">
                                <div class="col">
                                    <label for="zip-code" class="sr-only">Zip Code</label>
                                    <input class="form-control form-control-lg" v-model="zipCodeQuery" @keyup.enter="resetAndLookupZip" id="zip-code" type="text" value="" placeholder="Zip Code" autofocus />
                                </div>
                                <div class="col-auto">
                                    <button class="btn btn-secondary btn-lg" @click="resetAndLookupZip" value="Submit">Submit</button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="w-100"></div>

                    <div class="col-md-auto" v-if="!user">
                        <div class="card mt-4">
                            <div class="card-body">
                                <h2>Log In Now</h2>
                                <p>
                                    Log in to quickly re-order from any of your addresses on file.
                                </p>
                                <a class="btn btn-primary" href="/order/login?to=/order">Log In</a>
                            </div>
                        </div>
                    </div>

                    <div class="col-12 col-sm-12" v-else-if="customerAddresses && customerAddresses.length">
                        <h2 class="mt-5 my-4 text-white text-center">Or choose a previous address:</h2>
                        <div class="card-deck justify-content-center">
                            <div v-for="addr in customerAddresses" class="address card mb-3">
                                <div class="card-body">
                                    <h4 class="card-text" v-if="addr.zip_code" v-html="`${addr.zip_code.city}, ${addr.zip_code.zip_code} <br> <strong>${usd3(addr.price)}/gal`"></strong></h4>
                                    <p class="card-title" v-html="`<em>${addr.street} near ${addr.cross_street}</em>`"></p>
                                    <button class="btn btn-primary" @click="setAddress(addr, true)">
                                        Order Using This Address
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-12 col-sm-12" v-else>
                        <div class="card-deck justify-content-center mt-5">
                            <div class="address card mb-3">
                                <div class="card-body">
                                    <h4 class="card-text">No addresses on file</h4>
                                    <p>
                                        You don’t have any saved addresses. Complete an order and
                                        the address will be saved for next time.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>

        <div class="testimonial">
            <div class="container">
                <blockquote>
                    <p>What a great discovery! My niece-in-law let me know about Eastern Petroleum.
                    They delivered promptly as promised, and I think I saved about $150 on my first
                    delivery alone. So far, so good!</p>
                </blockquote>
                <p>Jill G. – Franklin, MA</p>
            </div>
        </div>

    </fieldset>

    <fieldset v-else>

        <div class="jumbotron text-center text-md-left">
            <div class="container with-transparency">
                <div class="row">
                    <div class="col-md-7 content">
                        <h1 class="display-4">
                            <b>
                                Purchase heating oil in
                            </b>
                            <span>
                                <em v-text="location"></em>
                                <i class="text-md-right">
                                    <button @click="reset()" class="btn btn-link p-0">Price based on delivery address. Not <span v-text="city"></span>? Change your zip code!</button>
                                </i>
                            </span>
                        </h1>

                        <div class="d-md-flex flex-column current-price">
                            <div class="price ">
                                <strong><sup>$</sup>{{ currentPrice }}</strong>
                            </div>
                        </div>
                    </div>

                    <div class="col-md-5 image text-center text-md-left">
                        <img class="img-fluid" v-if="heroImagePath" :src="heroImagePath" :alt="''" />
                    </div>
                </div>
            </div>
        </div>

        <div class="alert alert-primary text-center py-3 mb-0" v-if="!user">
            <div class="container">
                <div>
                    <p class="h5 align-middle d-md-inline mb-0">Are you a returning user? <a class="btn btn-primary ml-3" :href="'/order/login?to=' + $route.path">Log in now</a></p>
                </div>
            </div>
        </div>

        <div class="order-form">
            <div class="container">
                <h2><span v-text="location"></span> – <strong>start your order here</strong></h2>

                <div class="row">
                    <div class="col-md-8 mb-4">
                        <div v-if="showAddresses">
                            <h4>Your addresses:</h4>
                            <button v-for="address in addresses" class="btn btn-md mr-3" v-bind:class="{'btn-commercial': address.type == 'Commercial', 'btn-residential': address.type == 'Residential', 'btn-primary': isAddressSelected(address), 'btn-secondary': !isAddressSelected(address)}" @click="setAddress(address)">{{ address.street }}</button> <a href="/order" @click.prevent="setAddress('')">New Address</a>
                        </div>
                        <div v-if="!user" class="alert alert-warning">
                            <a href="/order/login?to=/order" class="alert-link">Log in now</a> to quickly re-order from any of your addresses on file.
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-md-8">
                        <form @submit.prevent="validate" method="post">
                            <div class="row">
                                <div class="col-md mb-4 mb-sm-0">
                                    <div class="form-group">
                                        <label class="label" for="street-address">Your Street Address</label>
                                        <input @change="clearError('street')" id="street-address" class="form-control" v-bind:class="{'is-invalid': errors.first('street')}" type="text" name="street" v-model="order.street" />
                                        <div class="invalid-feedback">
                                            Please provide a street address.
                                        </div>
                                    </div>
                                </div>

                                <div class="col-md mb-4 mb-sm-0">
                                    <div class="form-group">
                                        <label class="label" for="street-cross">Nearest Cross Street</label>
                                        <input @change="clearError('cross_street')" id="street-cross" class="form-control" v-bind:class="{'is-invalid': errors.first('cross_street')}" type="text" name="cross_street" v-model="order.cross_street" />
                                        <div class="invalid-feedback">
                                            Please provide a nearest cross street.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <hr />

                            <h3>Your Home</h3>

                            <div class="row">
                                <div class="col-md mb-4 mb-sm-0">
                                    <div class="d-flex justify-content-start mb-5">
                                        <div>
                                            <p class="label">Do you own or rent?</p>
                                            <div class="form-check-inline">
                                                <label class="form-check-label">
                                                    <input @change="clearError('ownership')" class="form-check-input" type="radio" name="ownership" v-model="order.ownership" value="Own" checked />
                                                    Own
                                                </label>
                                            </div>

                                            <div class="form-check-inline">
                                                <label class="form-check-label">
                                                    <input @change="clearError('ownership')" class="form-check-input" type="radio" name="ownership" v-model="order.ownership" value="Rent" />
                                                    Rent
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="d-flex justify-content-start">
                                        <div>
                                            <p class="label">What type of property is this?</p>
                                            <div>
                                                <div class="form-check-inline">
                                                    <label class="form-check-label">
                                                        <input @change="clearError('property_type')" class="form-check-input" type="radio" name="property_type" v-model="order.property_type" value="Residential" checked />
                                                        Residential
                                                    </label>
                                                </div>

                                                <div class="form-check-inline">
                                                    <label class="form-check-label">
                                                        <input @change="clearError('property_type')" class="form-check-input" type="radio" name="property_type" v-model="order.property_type" value="Commercial" />
                                                        Commercial
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="taxableWarning()" class="alert alert-warning" v-text="taxableWarning()"></div>

                                    <div v-if="taxableWarning()">
                                        <a href="forms/st_13.pdf">Download Small Business Energy Exemption Certificate</a>
                                    </div>
                                </div>

                                <div class="col-md mb-4 mb-sm-0">
                                    <div class="d-flex flex-column align-items-top">
                                            <p class="label">Select your fill pipe location</p>
                                            <div class="location-selector" v-bind:class="{ 'no-bueno': errors.first('location') }">
                                                <div class="back">
                                                    <input type="radio" name="location" v-model="order.location" value="Back Left"  />
                                                    <input @change="clearError('location')" type="radio" name="location" v-model="order.location" value="Back Center" />
                                                    <input @change="clearError('location')" type="radio" name="location" v-model="order.location" value="Back Right" />
                                                </div>

                                                <div class="left">
                                                    <input @change="clearError('location')" type="radio" name="location" v-model="order.location" value="Left Back" />
                                                    <input @change="clearError('location')" type="radio" name="location" v-model="order.location" value="Left Center" />
                                                    <input @change="clearError('location')" type="radio" name="location" v-model="order.location" value="Left Front" />
                                                </div>
                                                <div class="right">
                                                    <input @change="clearError('location')" type="radio" name="location" v-model="order.location" value="Right Back" />
                                                    <input @change="clearError('location')" type="radio" name="location" v-model="order.location" value="Right Center" />
                                                    <input @change="clearError('location')" type="radio" name="location" v-model="order.location" value="Right Front" />
                                                </div>

                                                <div class="front">
                                                    <input @change="clearError('location')" type="radio" name="location" v-model="order.location" value="Front Left" />
                                                    <input @change="clearError('location')" type="radio" name="location" v-model="order.location" value="Front Center" />
                                                    <input @change="clearError('location')" type="radio" name="location" v-model="order.location" value="Front Right" />
                                                </div>
                                            </div>
                                            <div v-if="order.location">Your Fill Location: <span class="badge badge-medium">{{ order.location }}</span></div>
                                            <div class="invalid-feedback">
                                                Please select where your fill location is.
                                            </div>
                                        </div>
                                </div>
                            </div>

                            <hr>

                            <h3>Your Oil</h3>

                            <div class="row align-items-top">

                                <div class="col-md mb-4 mb-sm-0">
                                    <div class="d-flex justify-content-start mb-5">
                                        <div>
                                            <div :class="{ 'no-bueno': errors.first('tank_size') }">
                                                <div class="form-group">
                                                    <label class="label">What is your oil tank size in gallons?</label>
                                                    <input @change="clearError('tank_size')"
                                                        class="form-control"
                                                        v-bind:class="{ 'is-invalid': errors.first('tank_size') }"
                                                        type="text"
                                                        :disabled="order.tank_size_unknown"
                                                        v-model="order.tank_size"
                                                        name="tank_size"
                                                        />

                                                    <small class="form-text text-muted">
                                                        (275g is standard size) We do not fill underground tanks.
                                                    </small>
                                                </div>
                                            </div>

                                            <label class="form-check-label">
                                                <input class="form-check-input" type="checkbox" v-model="order.tank_size_unknown">
                                                I'm not sure about the Tank Size
                                            </label>

                                            <div class="invalid-feedback">
                                                Please provide a tank size.
                                            </div>

                                        </div>
                                    </div>

                                    <div class="d-flex justify-content-start">
                                        <div>
                                            <div>
                                                <label class="label">What is your gauge reading now?</label>
                                                <select
                                                    class="form-control form-group"
                                                    v-bind:class="{ 'is-invalid': errors.first('gauge_reading') }"
                                                    v-model="order.gauge_reading">
                                                    <option disabled value="">Choose One</option>
                                                    <option value="1/8">1/8 full</option>
                                                    <option value="1/4">1/4 full</option>
                                                    <option value="3/8">3/8 full</option>
                                                    <option value="1/2">1/2 full</option>
                                                </select>
                                                <div class="invalid-feedback">
                                                    Please provide a gauge reading size.
                                                </div>
                                                <div v-if="guageWarning" class="alert alert-warning">
                                                    You may occur addition charges if your final
                                                    delivery is less than 150 gallons.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-md mb-4 mb-sm-0">
                                    <div class="d-flex justify-content-start">
                                        <div class="mb-4 mb-sm-0 w-100">
                                            <div class="form-group">
                                                <label class="label">How many gallons do you wish to order?</label>
                                                <input
                                                    @change="clearError('gallons')"
                                                    class="form-control"
                                                    v-bind:class="{ 'is-invalid': errors.first('gallons') }"
                                                    type="text"
                                                    :disabled="order.fill_tank"
                                                    name="gallons"
                                                    v-model="order.gallons"
                                                    />
                                                <div class="invalid-feedback">
                                                    Please provide a gallon amount.
                                                </div>
                                                <small v-text="smallOrderNotice" class="form-text text-muted">{{ smallOrderNotice }}</small>
                                            </div>
                                            <div class="form-check form-group">
                                                <div :class="{ invalid: errors.first('fill_tank') }">
                                                    <label class="form-check-label">
                                                        <input @change="clearError('fill_tank')" class="form-check-input" type="checkbox" v-model="order.fill_tank">
                                                        Fill My Tank
                                                    </label>
                                                </div>
                                            </div>

                                            <div v-show="errors.has('gallons')" v-text="errors.firstByRule('gallons', 'max_value')"></div>
                                            <div class="alert alert-warning" v-if="deliveryFeeWarning">{{ deliveryFeeWarning }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <hr />

                            <div class="row">
                                <div class="col-md">
                                    <div class="d-flex justify-content-start">
                                        <div>
                                            <p class="label">Need Prime</p>
                                            <div class="form-check form-check-inline mb-5">
                                                <label class="form-check-label">
                                                    <input type="checkbox" class="form-check-input" v-model="order.needs_prime" />
                                                    I have run out of oil and need the driver to prime
                                                    my burner for an additional {{primeFee}}.  I understand
                                                    that someone will have to be at the delivery site
                                                    to let the driver in and that if additional service
                                                    is needed to get the burner operating then I will
                                                    have to contact a burner technician.
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md mb-4 mb-sm-0">
                                    <div class="d-flex justify-content-start" :class="{ invalid: errors.first('delivery_method') }">
                                        <div>
                                            <p class="label">Delivery Method</p>
                                            <div class="form-check">
                                                <label class="form-check-label">
                                                    <input class="form-check-input" type="radio" name="delivery_method" v-model="order.delivery_method" value="Standard" />
                                                    Standard <span class="help">({{ zone.days_out }} business days)</span>
                                                </label>
                                            </div>
                                            <div class="form-check" v-if="showEmergencyDelivery">
                                                <label class="form-check-label">
                                                    <input class="form-check-input" type="radio" name="delivery_method" v-model="order.delivery_method" value="Emergency" />
                                                    Emergency <span class="help">(Next business day, {{ usd(zone.emergency_delivery_fee) }} fee)</span>
                                                </label>
                                            </div>
                                            <div v-if="showEmergencyDelivery && zipCode.zone.emergency_delivery_message" class="alert alert-danger">
                                               {{ zipCode.zone.emergency_delivery_message }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col">
                                    <p class="label">Automatic Delivery</p>
                                    <label class="form-check-label">
                                        <input class="form-check-input" type="checkbox" v-model="order.automatic_delivery">
                                        I am interested in setting up automatic delivery.
                                    </label>
                                    <div v-if="order.automatic_delivery" class="alert alert-info">
                                        You will receive an email explaining how automatic delivery works once this order is processed.
                                    </div>
                                </div>
                            </div>

                            <hr />

                            <div class="row">
                                <div class="col-md mb-4 mb-sm-0">
                                    <div class="form-group mb-0">
                                        <label class="label">Special Instructions</label>
                                        <textarea class="form-control" v-model="order.special_instructions"></textarea>
                                    </div>
                                </div>
                            </div>

                            <hr />

                            <div class="card mb-4" v-bind:class="{ 'is-invalid': errors.first('agree_to_terms') }">
                                <div class="card-body">
                                    <div class="form-check">
                                        <h4 class="card-title">Agreement</h4>
                                        <div class="form-check" v-bind:class="{ 'no-bueno': errors.first('agree_to_terms') }">
                                            <label class="form-check-label">
                                                <input @click="clearError('agree_to_terms')" class="form-check-input" type="checkbox" v-model="order.agree_to_terms" />
                                                I certify that my fuel tank and all connected piping are free of leaks, are functioning properly, and are properly permitted.
                                            </label>
                                        </div>
                                        <div class="invalid-feedback">
                                            Please agree to our terms.
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div v-if="taxableWarning()">
                                <hr />
                                <a href="forms/st_13.pdf">Download Small Business Energy Exemption Certificate</a>
                            </div>

                            <hr />
                            <div class="d-flex justify-content-start">
                                <button class="btn btn-primary" value="Continue">Continue</button>
                            </div>

                            <div v-if="errors.items.length" class="alert alert-danger">
                                <hr />
                                Please correct the following errors:
                                <ul>
                                    <li v-for="err in errors.items" v-text="err.msg.replace(/_/g, ' ')"></li>
                                </ul>
                            </div>
                        </form>
                    </div>

                    <div class="col-md-4 pl-md-5 d-none d-md-block">
                        <div class="sticky-wrapper">
                            <div class="sticky-container">
                                <div class="sticky-row">
                                    <div class="sticky-md-4 sticky-ml-auto">
                                        <div class="sticky-price">
                                            <div class="card">
                                                <div class="card-body">
                                                    <h4 v-if="this.estimate.total" class="card-title">{{ usd(this.estimate.total) }}</h4>
                                                    <h4 v-else>-</h4>
                                                    <p class="card-text">
                                                        <strong>{{ displayGallons }}</strong> gallons @ <strong>{{ usd3(adjustedPrice) }}</strong>/gal for <strong>{{ order.delivery_method }} delivery</strong> on or before the {{ deliveryWait }} business day of <strong>{{ deliveryDate }}</strong>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <needs-prime-modal v-if="showModal" @confirm="confirmPrime" @deny="showModal = false"></needs-prime-modal>
    </fieldset>
</template>
<script>
import '../../sticky';

import VeeValidate from 'vee-validate';
import numeral from 'numeral';
import moment from 'moment';
import NeedsPrimeModal from './NeedsPrimeModal';

const Validator = VeeValidate.Validator;

export default {
    components: {
        NeedsPrimeModal,
    },

    data() {
        return {
            type: 'zipCodes',
            zipCodeQuery: null,
            zipCodeError: '',
            errors: {},
            customerAddresses: {},
            recentOrders: {},
            stuck: false,
            showModal: false,
            primeConfirmed: false,
        }
    },

    computed: {
        smallOrderNotice() {
            if (this.zipCode.zone.medium_order_fee > 0) {
               return 'There will be a $' + this.zipCode.zone.medium_order_fee + ' small order fee for orders under 150 gallons';
            }
            if (this.zipCode.zone.small_order_fee > 0) {
               return 'There will be a $' + this.zipCode.zone.small_order_fee + ' small order fee for orders under 100 gallons';
            }
            return '';
        },
        deliveryFeeWarning() {
            return this.$root.deliveryFeeWarning;
        },

        guageWarning() {
            return (this.order.gauge_reading == '3/8' || this.order.gauge_reading == '1/2');
        },

        displayGallons() {
            return (this.estimate.gallons) ? this.estimate.gallons : 0;
        },

        showEmergencyDelivery() {
            return (this.zone.emergency_delivery_available == 1);
        },

        showAddresses() {
            return (this.addresses.length > 0);
        },

        order() {
            return this.$root.order;
        },

        zipCode() {
            return this.$root.zipCode;
        },

        zone() {
            return this.zipCode.zone;
        },

        user() {
            return this.$root.user;
        },

        priceSurcharge() {
            return this.$root.priceSurcharge(this.order.gallons);
        },

        addresses() {
            if (!this.customerAddresses.length) {
                return this.customerAddresses;
            }
            var zip = this.zipCode.zip_code;

            var filtered = this.customerAddresses.filter(function (el) {
                return el.zip_code.zip_code == zip;
            });

            return (filtered.length) ? filtered : [];
        },

        company() {
            return this.zone.company;
        },


        inspectionMessage() {
            if (this.recentOrders.length > 0) {
                return '';
            }
            return this.company.inspection_notice_message;
        },

        heroImagePath() {
            return this.zipCode.image_path;
        },

        heroImageAlt() {
            return this.zipCode.city + ' ' + this.zipCode.zip_code;
        },

        currentPrice() {
            return numeral(this.zone.price).format('0.000');
        },

        adjustedPrice() {
            return numeral(this.zone.price).add(this.priceSurcharge).format('0.000');
        },

        primeFee() {
            return this.usd(this.zone.prime_fee);
        },

        city() {
            return this.zipCode.city;
        },

        location() {
            return `${this.zipCode.city}, ${this.zipCode.state.name} ${this.zipCode.zip_code}`;
        },

        reading() {
            if (this.order.gauge_reading) {
                let fraction = this.order.gauge_reading.split('/');
                return fraction[0] / fraction[1];
            }

            return null;
        },

        deliveryDate() {
            return moment(this.estimate['delivery_date']).format('dddd, MMMM Do');
        },

        deliveryWait() {
            var businessDays = this.zone.days_out;

            if ('Emergency' == this.order.delivery_method) {
                return 'next';
            }
            /**
            //var current  = moment().startOf('day');
            //var estimate = moment(this.estimate['delivery_date']);
            while (estimate.diff(current, 'days') > 0) {
                switch (current.day()) {
                    case 0:
                    case 6:
                        break;

                    default:
                        businessDays++;
                        break;
                }
                console.log('++');
                current = current.add(1, 'days');
            }
            /**/
            var locale       = moment.localeData();
            return locale.ordinal(businessDays);
        },

        estimate() {
            return this.$root.estimate;
        },
    },

    created() {
        let zipParam = this.$route.params.zip;
        let addressId = this.$route.query.address;

        this.clearConfirmationLoaded();

        this.newValidator();

        if (this.user && addressId && addressId != 'undefined') {
            // try to load the address
            axios.get('/order/api/user-addresses')
                .then(response => {
                    this.customerAddresses = response.data.data;

                    for (let i = 0; i < this.customerAddresses.length; i++) {
                        if (this.customerAddresses[i].id == addressId) {
                            return this.setAddress(this.customerAddresses[i]);
                        }
                    }

                    // TODO bad addressId stuff
                    if (zipParam) {
                        this.zipCodeQuery = zipParam;
                        if ((window.performance && window.performance.navigation.type == 0) || !this.order || this.order.zip_code != zipParam) {
                            this.resetOrder();
                        }
                        this.lookupZip();
                    }
                });

        } else {
            if (zipParam) {
                this.zipCodeQuery = zipParam;
                if ((window.performance && window.performance.navigation.type == 0) || !this.order || this.order.zip_code != zipParam) {
                    this.resetOrder();
                }
                this.lookupZip();
            }

            if (this.user) {
                axios.get('/order/api/user-addresses')
                    .then(response => {
                        this.customerAddresses = response.data.data;

                        if (!this.zipCode && this.customerAddresses.length === 1) {
                            this.lookupZip(this.customerAddresses[0]);
                            this.setAddress(this.customerAddresses[0]);
                        }
                    });
            }
        }
    },

    mounted() {
        this.setTimer();
        this.$nextTick(() => {
            if (!this.stuck && $('.sticky-wrapper').length) {
                this.stuck = true;
                $('.sticky-wrapper').fix({startOffset: 16});
            }
        });
    },

    watch: {
        '$route'() {
            this.clearConfirmationLoaded();
        },
        zipCode() {
            if (this.zipCode) {
                this.$router.push({name: 'form-with-zip', params: {
                    zip: this.zipCode.zip_code,
                    slug: '-' + this.zipCode.city,
                }});

                document.title = `Cheap Heating Oil, ${this.zipCode.city}, ${this.zipCode.state.name} - EasternPetroleumOnline.com`;
                this.lookupOrders();
            }

            this.$nextTick(() => {
                if (!this.stuck && $('.sticky-wrapper').length) {
                    this.stuck = true;
                    $('.sticky-wrapper').fix({startOffset: 16});
                }
            });
        },

        'order.tank_size_unknown'() {
            if (this.order.tank_size_unknown) {
                this.order.tank_size = 275;
            } else {
                this.order.tank_size = '';
            }

            if (this.order.fill_tank && this.order.tank_size) {
                this.order.gallons = this.order.tank_size;
            } else {
                this.order.gallons = '';
            }
        },

        'order.fill_tank'() {
            if (this.order.fill_tank && this.order.tank_size) {
                this.order.gallons = this.order.tank_size;
            } else {
                this.order.gallons = '';
            }
        },
        'order.needs_prime'(newVal, oldVal) {
            if (newVal && !oldVal) {
                this.primeConfirmed = false;
            }
        }
    },

    methods: {
        confirmPrime() {
            this.showModal = false;
            this.primeConfirmed = true;
            this.validate();
        },
        clearConfirmationLoaded() {
            if (localStorage.getItem('confirmationLoaded') == 1 || sessionStorage.getItem('confirmationLoaded') == 1) {
                localStorage.setItem('confirmationLoaded', 0);
                sessionStorage.setItem('confirmationLoaded', 0);
            }
        },
        setTimer() {
            // Set the page/session to timeout after 60 minutes
            localStorage.setItem('timeout', Date.now() + (1000 * 60 * 60));
        },
        needsRefresh() {
            if (localStorage.getItem('timeout') > Date.now()) {
                return false;
            }
            return true;
        },
        cleanup() {
            if (localStorage.getItem('timeout') !== null) {
                localStorage.removeItem('timeout');
            }
        },
        clearError(field) {
            this.errors.items = this.errors.items.filter(err => err.field != field);
        },

        reset() {
            this.$root.zipCode = null;
            this.$router.push({name: 'form'});
        },


        taxableWarning() {
            if (this.order.property_type == 'Commercial') {
                return this.zipCode.state.commercial_tax_message;
            }
            return this.zipCode.state.residential_tax_message;
        },


        newValidator()
        {
            var validator = new Validator();

            // custom validation messages
            validator.updateDictionary({
                en: {
                    custom: {
                        gallons: {
                            max_value: (field, value) => `Based on your tank and gauge reading, you can only order up to ${value} gallons.`
                        }
                    }
                }
            });

            var rules = {
                street: { rules: { required: true } },
                cross_street: { rules: { required: true } },
                ownership: { rules: { required: true } },
                property_type: { rules: { required: true } },
                gauge_reading: { rules: { required: true } },
                tank_size: { rules: {
                    required: true,
                    numeric: true,
                    min_value: 10,
                } },
                delivery_method: { rules: { required: true } },
                location: { rules: { required: true } },
                gallons: { rules: {
                    required: true,
                    numeric: true,
                    min_value: 10,
                }},
                agree_to_terms: { rules: { in: [true] } },
            };

             _.forOwn(rules, (field, name) => {
                validator.attach(name, field.rules);
            });

            this.errors = validator.errors;

            return validator;
        },

        checkPriceStillValid(callback) {
            axios.get('/order/api/price', {params: {zip_code: this.order.zip_code}})
                .then(response => {
                    let price = response.data.data.current_price;
                    if (response.data.data.valid) {
                        return callback();
                    } else if (this.currentPrice == price) {
                        return callback();
                    } else {
                        // This is hacky
                        this.zipCode.price = price;
                        this.zipCode.zone.price = price;

                        this.errors.items.push({
                            msg: `The price has expired after 30 minutes. The current price is ${this.adjustedPrice} if you continue.`
                        });
                    }
                });
        },

        validate() {
            var validator = this.newValidator();

            if (this.needsRefresh()) {
                alert('Your session has expired. The page will refresh and you can try again.');
                window.location.reload();
                return;
            }

            if (this.order.needs_prime && !this.primeConfirmed) {
                this.showModal = true;
                return;
            } 

            this.order.transaction_id = '';
            this.order.invoice_number = '';

            // This is hacky - ensures that tank_size and gallons are set, using defaults if necssary.
            if (this.order.tank_size_unknown) {
                this.order.tank_size = 275;
            }
            if (this.order.fill_tank) {
                this.order.gallons = this.order.tank_size;
            }

            validator.validateAll(this.order)
                .then(result => {
                    if (result) {
                        this.cleanup();
                        this.checkPriceStillValid(() => {
                            this.$router.push({name: 'review'});
                        });
                    }
                });
        },

        resetOrder() {
            this.$root.order = {
                agree_to_terms: false,
                company_id: null,
                delivery_method: 'Standard',
                fill_tank: false,
                gallons: null,
                gauge_reading: "",
                invoice_number: '',
                needs_prime: false,
                special_instructions: null,
                tax_rate: null,
                transaction_id: '',
                zip_code: null,
            };
        },

        resetAddress() {
            this.order.address_id         = null;
            this.order.street             = "";
            this.order.cross_street       = "";
            this.order.location           = null;
            this.order.ownership          = 'Own';
            this.order.property_type      = 'Residential';
            this.order.tank_size          = null;
            this.order.tank_size_unknown  = false;
        },

        setAddress(address, gotoNext=false) {
            this.resetOrder();
            if (address) {
                if (gotoNext) {
                    this.zipCodeQuery = address.zip_code.zip_code;
                }

                if (!this.zipCode) {
                    this.lookupZip(address);
                }

                this.order.address_id         = address.id;
                this.order.street             = address.street;
                this.order.cross_street       = address.cross_street;
                this.order.location           = address.location;
                this.order.ownership          = address.ownership;
                this.order.property_type      = address.type;
                this.order.tank_size          = address.tank_size;
                this.order.tank_size_unknown  = (address.tank_size) ? false : true;
                this.order.zip_code           = address.zip_code;
            } else {
                this.resetAddress();
                this.order.zip_code = this.zipCode.zip_code;
            }
        },

        isAddressSelected(address) {
            return address.id == this.order.address_id;
        },

        resetAndLookupZip(address) {
            this.resetOrder();
            return this.lookupZip(address);
        },

        lookupZip(address) {

            // If the address isn't passed, it still might pass an object with
            // a 'isTrusted' key that normally is the second argument. This is
            // to make sure it's an address.
            if (address && !address.id) {
                address = null;
            }

            if (address) {
                this.zipCodeQuery = address.zip_code.zip_code;
            }

            if (this.zipCodeQuery) {
                axios.get('/order/api/search', {
                    params: {zip_code: this.zipCodeQuery }
                }).then(response => {
                    this.$root.zipCode       = response.data.data;

                    // If changing zip code, reset the zip code; otherwise persist data
                    if (this.order.zip_code != this.$root.zipCode.zip_code) {
                        this.order.zip_code = this.$root.zipCode.zip_code;

                        if (!address) {
                            this.resetAddress();
                        }

                        this.$router.push({name: 'form-with-zip', params: {
                            zip: response.data.data.zip_code,
                            slug: '-' + response.data.data.city,
                        }});

                        document.title = `Cheap Heating Oil, ${response.data.data.city}, ${response.data.data.state.name} - EasternPetroleumOnline.com`;
                    }

                    this.lookupOrders();
                }).catch(response => {
                    if (!response.data || !response.data.data) {
                        this.zipCodeError = `
                            We do not currently deliver to zip code ${this.zipCodeQuery}. Please enter another zip code.
                        `;
                    } else {
                        this.zipCodeError = `
                            We're sorry, but there's been a problem looking up zip code ${this.zipCodeQuery} in our database. Please try again.
                        `;
                    }
                });
            }
        },

        lookupOrders() {
            if (this.zipCode && this.user) {
                axios.get('/order/api/user-orders', {
                    params: {
                        zip_code: this.zipCode.zip_code,
                        start_date: moment().subtract('1', 'year').format('YYYY-MM-DD')
                    }
                } ).then(response => {
                    this.recentOrders = response.data.data;
                }).catch(response => {
                    // Debug
                });
            }
        },
    },
};
</script>
